import {
  convertPressure,
  convertSpeed,
  convertTemperature,
} from '../../../../../../../helpers/conversion';
import { getFormattedCatchTime } from '../../../../../../../helpers/date/getFormattedCatchTime';
import { getPressureIconValue } from '../../../../../../../helpers/getPressureIconValue';
import { UseUserInfo } from '../../../../../../../helpers/hooks/useUserInfo';
import { formatMoonIllumination } from '../../../../../../../helpers/weather/formatMoonIllumination';
import { AirPressureIcon } from '../../../../../../Icons/WeatherIcons/AirPressure/AirPressureIcon';
import {
  IMoonPhaseIconProps,
  MoonPhaseIcon,
} from '../../../../../../Icons/WeatherIcons/MoonPhase/MoonPhaseIcon';
import { SunPositionIcon } from '../../../../../../Icons/WeatherIcons/SunPosition/SunPositionIcon';
import { WaterTemperatureIcon } from '../../../../../../Icons/WeatherIcons/WaterTemperature/WaterTemperatureIcon';
import { WeatherConditionIcon } from '../../../../../../Icons/WeatherIcons/WeatherCondition/WeatherConditionIcon';
import { WindIcon } from '../../../../../../WindIcon/WindIcon';
import type { CatchConditions } from '../../../../../types/CatchConditions';
import { iconsSize } from '../iconsSize';

interface CatchInfoCardData {
  bottomText: string | undefined;
  cardDescription: string;
  iconComponent: false | React.JSX.Element;
  iconText: string;
  shouldRenderCatchInfoCard: boolean;
}

export const getCatchesInfoCardsData = (
  catchConditions: CatchConditions,
  currentUser: UseUserInfo,
): CatchInfoCardData[] => {
  const { weatherAndMarineReading, sunPosition } = catchConditions;
  const { caughtAtLocalTimeZone, moonIllumination } = catchConditions;
  const weatherCondition = weatherAndMarineReading?.weatherCondition;
  const conditionCode = Number(weatherCondition?.worldWeatherOnlineIdentifier) || undefined;
  const weatherValue = weatherCondition?.localizedValue;
  const airTemperature = weatherAndMarineReading?.airTemperature;
  const airPressure = weatherAndMarineReading?.airPressure;
  const formattedCatchTime = getFormattedCatchTime(caughtAtLocalTimeZone);
  const waterTemperature = weatherAndMarineReading?.waterTemperature;
  const typedMoonIllumination = moonIllumination?.toLowerCase() as IMoonPhaseIconProps['moonPhase'];
  const windSpeed = weatherAndMarineReading?.windSpeed;
  const windDirection = weatherAndMarineReading?.windDirection?.shortLocalizedValue;
  const degrees = weatherAndMarineReading?.windDirection?.degrees;

  return [
    {
      bottomText: weatherValue,
      cardDescription: 'Weather',
      iconComponent: !!conditionCode && (
        <WeatherConditionIcon conditionCode={conditionCode} height={iconsSize} width={iconsSize} />
      ),
      iconText:
        typeof airTemperature === 'number' ? convertTemperature(airTemperature, currentUser) : '-',
      shouldRenderCatchInfoCard: !!conditionCode && !!weatherValue,
    },
    {
      bottomText: 'Water Temperature',
      cardDescription: 'Water Temperature',
      iconComponent: !!waterTemperature && (
        <WaterTemperatureIcon height={iconsSize} width={iconsSize} />
      ),
      iconText:
        typeof waterTemperature === 'number'
          ? convertTemperature(waterTemperature, currentUser)
          : '-',
      shouldRenderCatchInfoCard: !!waterTemperature,
    },
    {
      bottomText: 'Wind',
      cardDescription: 'Wind',
      iconComponent: !!degrees && <WindIcon angle={degrees} height={iconsSize} width={iconsSize} />,
      iconText:
        typeof windSpeed === 'number'
          ? `${convertSpeed(windSpeed, currentUser)} ${windDirection}`
          : '-',
      shouldRenderCatchInfoCard: !!windSpeed && !!windDirection && !!degrees,
    },
    {
      bottomText: formattedCatchTime || '',
      cardDescription: 'Time of day',
      iconComponent: !!sunPosition && (
        <SunPositionIcon height={iconsSize} sunPosition={sunPosition} width={iconsSize} />
      ),
      iconText: '',
      shouldRenderCatchInfoCard: !!sunPosition,
    },
    {
      bottomText: formatMoonIllumination(typedMoonIllumination),
      cardDescription: 'Moon Phase',
      iconComponent: (
        <MoonPhaseIcon moonPhase={typedMoonIllumination} height={iconsSize} width={iconsSize} />
      ),
      iconText: '',
      shouldRenderCatchInfoCard: !!typedMoonIllumination,
    },
    {
      bottomText: typeof airPressure === 'number' ? convertPressure(airPressure, currentUser) : '',
      cardDescription: 'Air Pressure',
      iconComponent: !!airPressure && (
        <AirPressureIcon
          value={getPressureIconValue(airPressure)}
          height={iconsSize}
          width={iconsSize}
        />
      ),
      iconText: '',
      shouldRenderCatchInfoCard: !!airPressure,
    },
  ];
};
